import { useState } from "react";
import { IconMail, IconPlus, IconX } from "@tabler/icons-react";
import { AxiosError } from "axios";

import { programService } from "../../services/program.service";
import { RichTextEditor } from "../Editor/RichTextEditor";
import { getRichTextLimitError } from "@/utils/richText";
import { Button } from "@/components/ui/button";
import { ActionDialog, DialogNotice, DialogTextButton } from "../common/ActionDialog";

interface ApiErrorResponse {
  success: boolean;
  message: string;
  emailsWithInvitations?: string[];
}

interface InviteAffiliateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function InviteAffiliateModal({
  isOpen,
  onClose,
}: InviteAffiliateModalProps) {
  const [emails, setEmails] = useState<string[]>([""]);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleEmailChange = (index: number, value: string) => {
    const next = [...emails];
    next[index] = value;
    setEmails(next);
  };

  const addEmailField = () => setEmails([...emails, ""]);

  const removeEmailField = (index: number) => {
    if (emails.length === 1) return;
    setEmails(emails.filter((_, i) => i !== index));
  };

  const handleError = (err: unknown) => {
    if (err instanceof AxiosError) {
      const errorData = err.response?.data as ApiErrorResponse;

      if (errorData?.message) {
        switch (errorData.message) {
          case "Program not found create one first":
            setError("Create a program before inviting affiliates.");
            break;
          case "Email already invited":
            setError("This email has already been invited.");
            break;
          case "Email already registered":
            setError("This email is already registered as an affiliate.");
            break;
          case "Some emails already have pending invitations": {
            const list = errorData.emailsWithInvitations?.join(", ");
            setError(`Already invited: ${list}. Remove them and try again.`);
            break;
          }
          default:
            setError(errorData.message);
        }
      } else if (err.response?.status === 400) {
        setError("One of the email addresses is not valid.");
      } else if (err.code === "ERR_NETWORK") {
        setError("Network error. Check your connection and try again.");
      } else {
        setError("The invitations could not be sent. Try again.");
      }
    } else {
      setError("Something went wrong. Try again.");
    }
  };

  const reset = () => {
    setEmails([""]);
    setMessage("");
    setError(null);
    setSuccess(null);
  };

  const close = () => {
    if (isLoading) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const validEmails = emails.map((email) => email.trim()).filter(Boolean);

      if (validEmails.length === 0) {
        setError("Enter at least one email address.");
        return;
      }

      const messageError = getRichTextLimitError(message, 6000);
      if (messageError) {
        setError(messageError);
        return;
      }

      for (const email of validEmails) {
        await programService.inviteAffiliate(email, message || undefined);
      }

      setSuccess(
        validEmails.length === 1 ? "Invitation sent." : `${validEmails.length} invitations sent.`
      );
      setTimeout(() => {
        reset();
        onClose();
      }, 1800);
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ActionDialog
      open={isOpen}
      onClose={close}
      title="Invite affiliates"
      footer={
        <>
          <DialogTextButton onClick={close} disabled={isLoading}>
            Cancel
          </DialogTextButton>
          <Button type="submit" form="invite-affiliates-form" disabled={isLoading || !!success}>
            {isLoading ? "Sending" : "Send invitations"}
          </Button>
        </>
      }
    >
      <form id="invite-affiliates-form" onSubmit={handleSubmit} className="space-y-5">
        <div>
          <div className="space-y-2">
            {emails.map((email, index) => (
              <div key={index} className="relative">
                <IconMail
                  size={16}
                  stroke={1.75}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  placeholder="name@example.com"
                  autoFocus={index === 0}
                  className="h-9 w-full rounded-md border border-input bg-background pl-10 pr-9 text-[13px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                {emails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEmailField(index)}
                    aria-label="Remove this email"
                    className="absolute right-2 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground"
                  >
                    <IconX size={14} stroke={1.75} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addEmailField}
            className="mt-2 inline-flex items-center gap-1 rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <IconPlus size={14} stroke={1.75} />
            Add another email
          </button>
        </div>

        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-foreground">
            Message <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <RichTextEditor
            content={message}
            onChange={setMessage}
            placeholder="Anything the affiliate should know before joining"
            maxLength={6000}
          />
        </div>

        {error && <DialogNotice tone="error">{error}</DialogNotice>}
        {success && <DialogNotice tone="success">{success}</DialogNotice>}
      </form>
    </ActionDialog>
  );
}
